import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const HeroSection = () => {
  const { t, isRTL } = useLanguage();
  
  const highlights = [
    t('hero.highlight1'),
    t('hero.highlight2'),
    t('hero.highlight3'),
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30"></div>
      <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-1/2 h-full bg-gradient-to-${isRTL ? 'r' : 'l'} from-red-50/30 to-transparent`}></div>
      
      {/* Decorative Elements */}
      <div className={`absolute top-40 ${isRTL ? 'right-10' : 'left-10'} w-72 h-72 bg-blue-100/40 rounded-full blur-3xl`}></div>
      <div className={`absolute bottom-20 ${isRTL ? 'left-20' : 'right-20'} w-96 h-96 bg-red-100/20 rounded-full blur-3xl`}></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-20 items-center">
          {/* Content */}
          <div className={`space-y-6 lg:space-y-8 ${isRTL ? 'lg:order-2' : ''}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-100 rounded-full">
              <span className="w-2 h-2 bg-brand-red rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-brand-blue">{t('hero.badge')}</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-slate-800 leading-tight">
              {t('hero.title1')}
              <span className="block text-brand-blue mt-2">{t('hero.title2')}</span>
            </h1>
            
            <p className="text-base lg:text-lg text-slate-600 max-w-xl leading-relaxed">
              {t('hero.description')}
            </p>

            {/* Mobile Image - Shows only on mobile/tablet */}
            <div className="lg:hidden relative">
              <div className="bg-white rounded-2xl shadow-xl p-4 border border-slate-100">
                <div className="aspect-[4/3] bg-gradient-to-br from-blue-50 to-red-50/30 rounded-xl overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=450&fit=crop" 
                    alt="Medical Supplies"
                    className="w-full h-full object-cover rounded-xl"
                  />
                </div>
              </div>
            </div>

            {/* Highlights */}
            <div className="flex flex-wrap gap-3 lg:gap-4">
              {highlights.map((item, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="w-4 h-4 lg:w-5 lg:h-5 text-brand-red flex-shrink-0" />
                  <span className="text-xs sm:text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-3 lg:gap-4 pt-2 lg:pt-4">
              <Button 
                onClick={() => scrollToSection('#services')}
                className="bg-brand-red hover:bg-red-700 text-white px-6 lg:px-8 py-5 lg:py-6 text-sm lg:text-base shadow-xl shadow-red-600/20 hover:shadow-red-600/30 transition-all duration-300 hover:-translate-y-0.5"
              >
                {t('hero.cta1')}
                <ArrowIcon className={`w-4 h-4 lg:w-5 lg:h-5 ${isRTL ? 'mr-2' : 'ml-2'}`} />
              </Button>
              <Button 
                variant="outline"
                onClick={() => scrollToSection('#about')}
                className="px-6 lg:px-8 py-5 lg:py-6 text-sm lg:text-base border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all duration-300"
              >
                {t('hero.cta2')}
              </Button>
            </div>
          </div>

          {/* Desktop Visual Element - Hidden on mobile */}
          <div className={`relative hidden lg:block ${isRTL ? 'lg:order-1' : ''}`}>
            <div className="relative z-10">
              <div className="bg-white rounded-3xl shadow-2xl shadow-slate-200/50 p-8 border border-slate-100">
                <div className="aspect-square bg-gradient-to-br from-blue-50 to-red-50/30 rounded-2xl flex items-center justify-center overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=600&fit=crop" 
                    alt="Medical Supplies"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
            </div>
            
            {/* Floating Cards */}
            <div className={`absolute -top-4 ${isRTL ? '-right-8' : '-left-8'} bg-white rounded-2xl shadow-xl p-4 border border-slate-100 animate-float`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-brand-blue" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{t('hero.certified')}</p>
                  <p className="text-xs text-slate-500">{t('hero.certifiedDesc')}</p>
                </div>
              </div>
            </div>
            
            <div className={`absolute -bottom-4 ${isRTL ? '-left-4' : '-right-4'} bg-white rounded-2xl shadow-xl p-4 border border-slate-100 animate-float-delayed`}>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                  <span className="text-2xl font-bold text-brand-red" dir="ltr">10+</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{t('hero.experience')}</p>
                  <p className="text-xs text-slate-500">{t('hero.experienceDesc')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
